import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const STORAGE_KEY = "fl_admin_pw";
const API = process.env.REACT_APP_BACKEND_URL;

export default function Admin() {
  const [password, setPassword] = useState(localStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchStats = async (pw) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/stats`, {
        headers: { "X-Admin-Password": pw },
      });
      if (res.status === 401) {
        setError("Incorrect password");
        setAuthed(false);
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      const data = await res.json();
      setStats(data);
      setAuthed(true);
      localStorage.setItem(STORAGE_KEY, pw);
    } catch (e) {
      setError("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  // Auto-login if password is in storage
  useEffect(() => {
    if (password) {
      fetchStats(password);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    fetchStats(password);
  };

  const handleLogout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setPassword("");
    setAuthed(false);
    setStats(null);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0B2545] flex items-center justify-center p-6">
        <Card className="w-full max-w-sm p-8 bg-white">
          <h1 className="text-2xl font-heading font-black text-[#0B2545]">Admin Login</h1>
          <p className="text-sm text-slate-500 mt-1">Fairway Lab dashboard</p>
          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <Input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input"
              autoFocus
            />
            {error && <p className="text-sm text-red-600" data-testid="admin-error">{error}</p>}
            <Button
              type="submit"
              className="w-full bg-[#0B2545] hover:bg-[#0B2545]/90"
              disabled={loading || !password}
              data-testid="admin-login-btn"
            >
              {loading ? "Loading..." : "Sign In"}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 sm:p-10">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-black text-[#0B2545]">Admin Dashboard</h1>
            <p className="text-slate-500 mt-1">Fairway Lab — live metrics</p>
          </div>
          <Button variant="outline" onClick={handleLogout} data-testid="admin-logout-btn">Sign Out</Button>
        </div>

        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <StatCard label="Total Users" value={stats.total_users} testId="stat-total-users" />
            <StatCard label="New Users Today" value={stats.users_today} testId="stat-users-today" />
            <StatCard label="New Users This Week" value={stats.users_this_week} testId="stat-users-week" />
            <StatCard label="Total Sessions Generated" value={stats.total_sessions} testId="stat-total-sessions" />
          </div>
        )}

        <div className="mt-8">
          <Button
            variant="ghost"
            onClick={() => fetchStats(password)}
            disabled={loading}
            data-testid="admin-refresh-btn"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, testId }) {
  return (
    <Card className="p-7 bg-white border border-slate-200" data-testid={testId}>
      <div className="text-sm font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-3 text-5xl font-heading font-black text-[#0B2545] tabular-nums">{value ?? 0}</div>
    </Card>
  );
}
